import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Typography,
} from "@mui/material";
import {
  CommonButton,
  CustomDownIcon,
  CustomRequiredInputLabel,
  CustomUpIcon,
} from "../../common/StyledComponents/StyledFields";
import { groupingStyles } from "../ExpenseRequest/ExpenseRequest";
import CustomSelectField from "../../common/CustomSelectField/CustomSelectField";
import { useFormik } from "formik";
import {
  apiRequest,
  catchErrorMessageHandler,
  generateYearObject,
} from "../../../infrastructure/backendService";
import { useEffect, useState } from "react";
import apiPath from "../../../config/apiPaths.json";
import toast from "react-hot-toast";
import ErrorPopup from "../../common/ErrorPopup";
import styles from "../ViewRequests/ViewRequests.module.scss";
import { useAppDispatch, useAppSelector } from "../../../app/hook";
import { addExpenseCategories } from "../../../features/epxenseRequestSlice";
import APILoading from "../../common/APILoading/APILoading";
import ButtonLoader from "../../common/ButtonLoader/ButtonLoader";

const ExpenseReport = () => {
  const dispatch = useAppDispatch();
  const { expenseCategories } = useAppSelector((state) => state.expenseRequest);

  const yearsList = [...generateYearObject(2003)];
  const monthsList = [
    { label: "Select", value: 13 },
    { label: "January", value: 1 },
    { label: "February", value: 2 },
    { label: "March", value: 3 },
    { label: "April", value: 4 },
    { label: "May", value: 5 },
    { label: "June", value: 6 },
    { label: "July", value: 7 },
    { label: "August", value: 8 },
    { label: "September", value: 9 },
    { label: "October", value: 10 },
    { label: "November", value: 11 },
    { label: "December", value: 12 },
  ];

  const monthNames = [
    "January",
    "February",
    "March",
    "April",
    "May",
    "June",
    "July",
    "August",
    "September",
    "October",
    "November",
    "December",
  ];

  const [expenseSummary, setExpenseSummary] = useState<any>(null);
  const [expandedPanel, setExpandedPanel] = useState(null);
  const [subExpenseCategoryData, setSubExpenseCategoryData] =
    useState<any>(null);
  const [isAPILoading, setIsAPILoading] = useState(false);
  const [isExcelLoading, setIsExcelLoading] = useState(false);

  const formik = useFormik({
    initialValues: {
      year: new Date()?.getFullYear()?.toString(),
      month: 13,
    },
    onSubmit(values) {},
    enableReinitialize: true,
  });

  const handleFieldChange = (field: string, value: any) => {
    formik.setFieldValue(field, value);
  };

  const ExportToExcel = async () => {
    setIsExcelLoading(true);
    apiRequest({
      method: "GET",
      endpoint: apiPath.getExcelExpenseReport?.replace(
        "{year}",
        formik.values.year
      ),
      includeToken: true,
      params: {
        month: formik.values.month,
      },
      responseType: "arraybuffer",
    })
      .then((res) => {
        const blob = new Blob([res?.data], {
          type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
        });
        const link = document.createElement("a");
        link.download = `Expense_Report_${formik.values.year}${
          formik.values.month !== 13
            ? `_${monthNames[formik.values.month - 1]}`
            : ""
        }.xlsx`;
        link.href = window.URL.createObjectURL(blob);
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        setIsExcelLoading(false);
      })
      .catch((err) => {
        setIsExcelLoading(false);
        toast.custom(
          <ErrorPopup showMessage={catchErrorMessageHandler(err)} />
        );
      });
  };

  const getExpenseCategories = () => {
    apiRequest({
      method: "GET",
      endpoint: apiPath.expenseCategories,
      includeToken: true,
    })
      .then((res) => {
        dispatch(addExpenseCategories(res?.data));
      })
      .catch((err) => {
        toast.custom(
          <ErrorPopup showMessage={catchErrorMessageHandler(err)} />
        );
      });
  };

  const getExpenseSummaryData = () => {
    apiRequest({
      method: "GET",
      endpoint: apiPath.getExpenseSummary?.replace(
        "{year}",
        formik.values.year
      ),
      includeToken: true,
      params: {
        month: formik.values.month,
      },
    })
      .then((res) => {
        setExpenseSummary(res?.data);
      })
      .catch((err) => {
        toast.custom(
          <ErrorPopup showMessage={catchErrorMessageHandler(err)} />
        );
      });
  };

  const getExpenseReportsByAccounts = (accountId: string) => {
    setIsAPILoading(true);
    apiRequest({
      method: "GET",
      endpoint: apiPath.getExpensesByAccount
        ?.replace("{year}", formik.values.year)
        ?.replace("{accountId}", accountId),
      includeToken: true,
      params: {
        month: formik.values.month,
      },
    })
      .then((res) => {
        setSubExpenseCategoryData(res?.data);
        setIsAPILoading(false);
        const element = document.getElementById(accountId);
        if (element) {
          element?.scrollIntoView({ behavior: "smooth", block: "start" });
        }
      })
      .catch((err) => {
        setIsAPILoading(false);
        toast.custom(
          <ErrorPopup showMessage={catchErrorMessageHandler(err)} />
        );
      });
  };

  useEffect(() => {
    getExpenseSummaryData();
    setExpandedPanel(null);
  }, [formik.values.year, formik.values.month]);

  useEffect(() => {
    getExpenseCategories();
  }, []);

  const handleToggleChange = (index: any) => {
    setExpandedPanel(expandedPanel === index ? null : index);
    if (expandedPanel !== index) {
      getExpenseReportsByAccounts(index);
      setSubExpenseCategoryData(null);
    }
  };

  const renderHeaderSection = () => {
    return (
      <Box
        sx={{
          display: "flex",
          flexDirection: { xs: "column", sm: "row" },
          justifyContent: { xs: "flex-start", sm: "space-between" },
        }}
      >
        <Typography className="sub_heading">Expense Report</Typography>
        <CommonButton onClick={ExportToExcel}>
          <ButtonLoader
            isBtnLoading={isExcelLoading}
            btnText={"Export to Excel"}
          />
        </CommonButton>
      </Box>
    );
  };

  const renderFieldsComponent = () => {
    return (
      <Box className="common_box_shadow" sx={{ mt: "20px" }}>
        <Box sx={groupingStyles.groupedFieldsContainer}>
          <Box sx={groupingStyles.fieldContainer}>
            {CustomRequiredInputLabel("Select the Year", true)}
            <CustomSelectField
              handleDropdownChange={handleFieldChange}
              errorMessage={formik.touched.year ? formik.errors.year : ""}
              dropDownList={yearsList}
              value={formik.values.year}
              fieldName="year"
              placeholder="Select year"
              optionLabel="label"
              optionValue="value"
            />
          </Box>
          <Box sx={groupingStyles.fieldContainer}>
            {CustomRequiredInputLabel("Select the Month", true)}
            <CustomSelectField
              handleDropdownChange={handleFieldChange}
              errorMessage={formik.touched.month ? formik.errors.month : ""}
              dropDownList={monthsList}
              value={formik.values.month}
              fieldName="month"
              placeholder="Select month"
              optionLabel="label"
              optionValue="value"
            />
          </Box>
        </Box>
      </Box>
    );
  };

  const renderGrandSummary = () => {
    return (
      <Box sx={{ mt: "20px" }}>
        <Typography className={styles.grand_summary}>Grand Summary</Typography>
        <table
          className={styles.table_container}
          style={{ borderRadius: "0px" }}
        >
          <thead>
            <th>Total</th>
            <th className={styles.amount_text_black}>Total Budget</th>
            <th className={styles.amount_text_black}>Total Expense</th>
            <th className={styles.amount_text_black}>Total Remaining</th>
          </thead>
          <tbody>
            <tr>
              <td>{expenseSummary?.totalExpensePercentage ?? 0}% Used</td>
              <td className={styles.amount_text_black}>
                ${(expenseSummary?.totalBudgetAmount ?? 0)?.toFixed(2)}
              </td>
              <td className={styles.amount_text_black}>
                ${(expenseSummary?.totalExpenseAmount ?? 0)?.toFixed(2)}
              </td>
              <td className={styles.amount_text_black}>
                ($
                {Math.abs(expenseSummary?.totalRemainingAmount ?? 0)?.toFixed(
                  2
                )}
                )
              </td>
            </tr>
          </tbody>
        </table>
      </Box>
    );
  };

  const renderSubExpenseSummary = () => {
    return (
      <>
        {subExpenseCategoryData ? (
          <table
            className={styles.table_container}
            style={{ borderRadius: "0px" }}
          >
            <thead>
              <th>Account ID</th>
              <th>Description</th>
              <th>Month and Year</th>
              <th className={styles.amount_text_black}>Budget</th>
              <th className={styles.amount_text_black}>Expense</th>
              <th className={styles.amount_text_black}>Remaining</th>
            </thead>
            <tbody>
              {subExpenseCategoryData?.accountExpenseDetails?.map(
                (data: any, index: number) => {
                  return (
                    <tr key={`${data?.accountId}_${index}`}>
                      <td>{data?.accountId}</td>
                      <td>{data?.description}</td>
                      <td>{data?.monthAndYear ?? "-"}</td>
                      <td className={styles.amount_text_black}>
                        ${(data?.budgetAmount ?? 0)?.toFixed(2)}
                      </td>
                      <td className={styles.amount_text_black}>
                        ${(data?.expenseAmount ?? 0)?.toFixed(2)}
                      </td>
                      <td className={styles.amount_text_black}>
                        (${Math.abs(data?.remainingAmount ?? 0)?.toFixed(2)})
                      </td>
                    </tr>
                  );
                }
              )}
              <tr key="total" className={styles.table_summary_row}>
                <td colSpan={3}>
                  Total{" "}
                  {subExpenseCategoryData?.budgetExpenseSummary
                    ?.totalExpensePercentage ?? 0}
                  % used
                </td>
                <td className={styles.amount_text}>
                  $
                  {(
                    subExpenseCategoryData?.budgetExpenseSummary
                      ?.totalBudgetAmount ?? 0
                  )?.toFixed(2)}
                </td>
                <td className={styles.amount_text}>
                  $
                  {(
                    subExpenseCategoryData?.budgetExpenseSummary
                      ?.totalExpenseAmount ?? 0
                  )?.toFixed(2)}
                </td>
                <td className={styles.amount_text}>
                  ($
                  {Math.abs(
                    subExpenseCategoryData?.budgetExpenseSummary
                      ?.totalRemainingAmount ?? 0
                  )?.toFixed(2)}
                  )
                </td>
              </tr>
            </tbody>
          </table>
        ) : (
          <Typography className={styles.no_data_found}>
            No Data Found
          </Typography>
        )}
      </>
    );
  };

  const renderExpenseCategories = () => {
    return (
      <Box sx={{ mt: "20px" }}>
        {expenseCategories?.map((category: any, index: number) => {
          return (
            <Box
              className={styles.poojareportsLayout}
              key={index}
              sx={{ mt: "16px" }}
            >
              <Accordion
                sx={{
                  border:
                    expandedPanel === index
                      ? "1px solid #F05802"
                      : "1px solid #989898",
                  background: "#FFF9E4",
                  boxShadow: "none",
                  borderRadius: "8px",
                }}
                expanded={expandedPanel === category?.accountId}
                onChange={() => handleToggleChange(category?.accountId)}
              >
                <AccordionSummary
                  aria-controls={`panel-content`}
                  id={`panel--header`}
                >
                  <Box sx={{ display: "flex", alignItems: "center" }}>
                    <Box className={styles.iconstyles}>
                      {expandedPanel === category?.accountId ? (
                        <CustomUpIcon />
                      ) : (
                        <CustomDownIcon />
                      )}
                    </Box>
                    <Typography
                      variant="subtitle1"
                      className={styles.typoGraphyHeading}
                    >
                      {category?.accountId} | {category?.description}
                    </Typography>
                  </Box>
                </AccordionSummary>
                <AccordionDetails id={category?.accountId}>
                  {!isAPILoading ? renderSubExpenseSummary() : <APILoading />}
                </AccordionDetails>
              </Accordion>
            </Box>
          );
        })}
      </Box>
    );
  };

  return (
    <Box className={styles.expense_report_main}>
      {renderHeaderSection()}
      {renderFieldsComponent()}
      {renderGrandSummary()}
      {renderExpenseCategories()}
    </Box>
  );
};

export default ExpenseReport;
